import isNil from 'lodash/isNil';
import {
  getConditionalCellStyle,
  type ConditionalCellStyle,
} from 'common/visualizations/views/agGridReact/helpers/TableColumnFormatting';
import { type TableColumnFormat } from 'common/visualizations/views/agGridReact/types';
import { PIVOT_SEPARATOR } from 'common/visualizations/views/agGridReact/Constants';
import { isTablePrintingImprovementFormattingEnabled } from 'common/visualizations/helpers/VifSelectors';
import { CHECKBOX_COLUMN_TYPE } from 'common/authoring_workflow/constants';
import { type SoQLType } from 'common/types/soql';
import { type ViewColumn } from 'common/types/viewColumn';
import { type GroupExpansion, type TableColumn } from '../types';
import { type ConditionalCellStyles, type Row } from './type';

/**
 * Translates the agGrid conditional-formatting style ({ color, backgroundColor, fontWeight,
 * fontStyle }) into the jspdf-autotable cell style fragment the PDF renderers paint.
 */
const toPdfCellStyle = (
  style: ConditionalCellStyle,
): { textColor?: string; fillColor?: string; fontStyle?: string } => {
  const pdfStyle: { textColor?: string; fillColor?: string; fontStyle?: string } = {};

  if (style.color) {
    pdfStyle.textColor = style.color;
  }
  if (style.backgroundColor) {
    pdfStyle.fillColor = style.backgroundColor;
  }

  const isBold = style.fontWeight === 'bold';
  const isItalic = style.fontStyle === 'italic';
  if (isBold && isItalic) {
    pdfStyle.fontStyle = 'bolditalic';
  } else if (isBold) {
    pdfStyle.fontStyle = 'bold';
  } else if (isItalic) {
    pdfStyle.fontStyle = 'italic';
  }

  return pdfStyle;
};

interface CellConditionContext {
  /** evaluate the condition with the total-row flag set (grand total rows) */
  isTotal: boolean;
  /** evaluate the condition with the subtotal-row flag set (subtotal + header aggregation cells) */
  isSubtotal: boolean;
}

/**
 * Decides whether a given column's cell in a row actually shows a value and which row-type flags
 * to evaluate the condition under, mirroring GroupTableSectionRenderer's buildCellContents display
 * logic and the live grid's isSubtotalHeader handling. Returns null for cells that render blank
 * (e.g. grouped columns on leaf rows, ancestor group columns on a nested header), so we don't paint
 * empty cells.
 */
const resolveCellCondition = (
  row: Row,
  columnIndex: number,
  isGroupedColumn: boolean,
  showSubTotal: boolean,
  groupExpansion: GroupExpansion | undefined,
  isTablePrintingImprovementGroupingLayoutEnabled: boolean,
  isPivot: boolean,
): CellConditionContext | null => {
  if (row._rowType === 'header') {
    const groupLevel = row._groupLevel ?? 0;
    // the group-value cell sits at its own group level and renders like a normal value cell.
    if (columnIndex === groupLevel) {
      return { isTotal: false, isSubtotal: false };
    }

    if (isTablePrintingImprovementGroupingLayoutEnabled && isPivot && columnIndex > groupLevel) {
      return { isTotal: false, isSubtotal: false };
    }

    // when subtotals are hidden (or the group is collapsed) the header row also surfaces
    // aggregation values to the right of the group level; the grid treats those as subtotals.
    const showsAggregation = (!showSubTotal || groupExpansion === 'collapsed') && columnIndex > groupLevel;
    return showsAggregation ? { isTotal: false, isSubtotal: true } : null;
  }

  // grouped columns repeat nothing on subtotal/total/leaf rows, so their cells render blank.
  if (isGroupedColumn) {
    return null;
  }

  return { isTotal: row._rowType === 'total', isSubtotal: row._rowType === 'subTotal' };
};

/**
 * Coerces a raw cell value into what the live grid's conditional evaluator expects:
 *  - nil values become '' (the live grid uses `params.data[field] ?? ''`); this keeps empty cells
 *    from matching `moment(undefined)` date ranges or other accidental hits.
 *  - checkbox values are lower-cased to the raw `true`/`false` literal the condition stores.
 */
const normalizeCellValue = (value: unknown, columnType: SoQLType): string | number => {
  if (isNil(value)) {
    return '';
  }
  if (columnType === CHECKBOX_COLUMN_TYPE) {
    return String(value).toLowerCase();
  }
  return value as string | number;
};

/** A table column that has conditional formatting configured, paired with its position and the
 * base SoQL fieldName its column format / view column are keyed by. */
interface ConditionalColumn {
  column: TableColumn;
  index: number;
  formatKey: string;
}

/**
 * Builds the per-cell jspdf conditional styles for a single row, keyed by fieldName. Returns
 * undefined when no cell in the row matched a condition, so the caller can leave the row untouched.
 */
const processRowStyles = (
  row: Row,
  conditionalColumns: ConditionalColumn[],
  columnFormats: { [key: string]: TableColumnFormat },
  viewColumnByField: Map<string, ViewColumn>,
  showSubTotal: boolean,
  groupExpansion: GroupExpansion | undefined,
  isTablePrintingImprovementGroupingLayoutEnabled: boolean,
  isPivot: boolean,
): ConditionalCellStyles | undefined => {
  let conditionalStyles: ConditionalCellStyles | undefined;

  conditionalColumns.forEach(({ column, index, formatKey }) => {
    const condition = resolveCellCondition(
      row,
      index,
      !!column.isGrouped,
      showSubTotal,
      groupExpansion,
      isTablePrintingImprovementGroupingLayoutEnabled,
      isPivot,
    );
    if (!condition) {
      return;
    }

    const viewColumn = viewColumnByField.get(formatKey);
    // the boolean/checkbox condition branch keys off the column's literal SoQL dataTypeName.
    // tableColumns carry the same dataTypeName the live grid reads from dataset metadata, so use
    // it as the source of truth and keep the view column only for its linear-gradient range bounds.
    const evalColumn = { ...(viewColumn ?? {}), dataTypeName: column.type } as ViewColumn;
    const value = normalizeCellValue(row[column.field], column.type);
    const style = getConditionalCellStyle(
      columnFormats[formatKey],
      value,
      evalColumn,
      condition.isTotal,
      condition.isSubtotal,
    );
    if (!style) {
      return;
    }

    const pdfStyle = toPdfCellStyle(style);
    if (!Object.keys(pdfStyle).length) {
      return;
    }

    conditionalStyles = { ...(conditionalStyles ?? {}), [column.field]: pdfStyle };
  });

  return conditionalStyles;
};

/**
 * Pre-evaluates per-cell conditional formatting on the main thread (where `window`/locale exist)
 * and stashes the resulting jspdf cell styles on each row keyed by fieldName, so the PDF print
 * worker — which can't run the window-dependent agGrid formatter — just paints them.
 *
 * Runs on the *unformatted* rows (before gridDataFormatter), so number/currency/percent values are
 * still the raw numbers the conditional evaluator can parse — matching what the live grid evaluates.
 * Reuses the live table's conditional evaluator (`getConditionalCellStyle`) so the print matches the
 * grid, and only styles cells that render a value (group-label cells, subtotal/total/leaf value
 * cells) — blank grouped cells are left untouched.
 */
export const processConditionalCellStyles = (
  rows: Row[],
  tableColumns: TableColumn[],
  columnFormats: { [key: string]: TableColumnFormat },
  viewColumns: ViewColumn[] | undefined,
  showSubTotal: boolean,
  groupExpansion: GroupExpansion | undefined,
  isTablePrintingImprovementGroupingLayoutEnabled = false,
  isPivot = false,
): Row[] => {
  if (!isTablePrintingImprovementFormattingEnabled()) {
    return rows;
  }

  // pivot value columns carry a synthetic `pivotKey@@…@@baseField` id, but the column format and
  // the view column are keyed by the base SoQL fieldName (the segment after the last separator),
  // mirroring the live grid's getFieldName -> pivotValueColumn resolution.
  const conditionalColumns = tableColumns
    .map((column, index) => ({
      column,
      index,
      formatKey: column.field.split(PIVOT_SEPARATOR).pop() ?? column.field,
    }))
    .filter(({ formatKey }) => (columnFormats[formatKey]?.conditionalConfig?.length ?? 0) > 0);
  if (!conditionalColumns.length) {
    return rows;
  }

  const viewColumnByField = new Map((viewColumns ?? []).map((column) => [column.fieldName, column]));

  return rows.map((row) => {
    const conditionalStyles = processRowStyles(
      row,
      conditionalColumns,
      columnFormats,
      viewColumnByField,
      showSubTotal,
      groupExpansion,
      isTablePrintingImprovementGroupingLayoutEnabled,
      isPivot,
    );
    return conditionalStyles ? { ...row, _conditionalStyles: conditionalStyles } : row;
  });
};
